'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const coreFeatures = [
  {
    num: '01',
    icon: 'MapIcon',
    label: 'AI-powered Multi-stop Route Optimization',
    desc: 'Automatically compute the most efficient delivery sequence per vehicle, factoring in traffic, time windows, vehicle capacity and priority orders — cutting unnecessary distance and fuel cost.',
    tag: 'Route Intelligence',
  },
  {
    num: '02',
    icon: 'CubeIcon',
    label: 'Load Balancing & Capacity Optimization',
    desc: 'Intelligently distribute parcels across your fleet based on vehicle capacity, weight limits and delivery zones — maximizing utilization and eliminating empty or under-loaded runs.',
    tag: 'Fleet Efficiency',
  },
  {
    num: '03',
    icon: 'ClockIcon',
    label: 'ML-powered ETA Prediction',
    desc: 'Machine learning models trained on historical delivery data, traffic patterns and driver behavior provide accurate per-stop ETAs shared in real time with customers and dispatchers.',
    tag: 'Predictive ML',
  },
  {
    num: '04',
    icon: 'ChartPieIcon',
    label: 'Demand Forecasting via Historical Analysis',
    desc: 'Analyze months of delivery data to predict volume spikes, seasonal surges and route saturation — enabling proactive fleet and staff planning well ahead of time.',
    tag: 'Forecasting',
  },
  {
    num: '05',
    icon: 'BuildingStorefrontIcon',
    label: 'Warehouse Management System Integration',
    desc: 'Connect with your existing WMS to sync outbound orders, automate manifest generation, trigger driver dispatch and reconcile deliveries — zero manual data entry.',
    tag: 'WMS Connect',
  },
  {
    num: '06',
    icon: 'CurrencyRupeeIcon',
    label: 'Per-delivery Cost Analytics',
    desc: 'Break down operational costs at individual delivery level — fuel, driver time, distance, failed attempts — giving complete financial visibility and control over logistics margins.',
    tag: 'Cost Intelligence',
  },
];

const pipelineSteps = [
  {
    icon: 'InboxIcon',
    title: 'Order Received',
    desc: 'eCommerce, API, WhatsApp, manual entry or WMS outbound — all channels in one queue',
  },
  {
    icon: 'BoltIcon',
    title: 'AI Planning',
    desc: 'Routes optimized, loads balanced and drivers auto-assigned by the dispatch engine',
  },
  {
    icon: 'TruckIcon',
    title: 'Active Delivery',
    desc: 'Driver navigates multi-stop route with live POD capture and customer notifications',
  },
  {
    icon: 'ChartBarIcon',
    title: 'Cost & Insights',
    desc: 'Per-delivery cost, ETA accuracy, driver performance and demand trends — all tracked',
  },
];

const platformBenefits = [
  'Lower cost per delivery through AI route optimization',
  'Zero paperwork with digital POD and e-signature',
  'Full customer trust via live tracking and notifications',
  'Peak fleet utilization with intelligent load balancing',
  'Proactive planning with demand forecasting',
  'Seamless WMS integration — no manual data entry',
  'SLA compliance through real-time breach alerts',
  'Data-driven decisions with per-delivery cost analytics',
];

const platformCapabilities = [
  { icon: 'MapPinIcon', label: 'Real-time Shipment Tracking' },
  { icon: 'DocumentTextIcon', label: 'Digital Proof of Delivery' },
  { icon: 'BoltIcon', label: 'Automated Dispatch Engine' },
  { icon: 'HomeModernIcon', label: 'Multi-depot Hub Management' },
  { icon: 'UserGroupIcon', label: 'Driver Performance Scoring' },
  { icon: 'BellAlertIcon', label: 'SLA Breach Alerts' },
  { icon: 'ArrowPathIcon', label: 'Returns & Reverse Logistics' },
  { icon: 'ShieldCheckIcon', label: 'Geo-fence & Zone Control' },
];

const dispatcherFeatures = [
  'Live Map with All Active Deliveries',
  'Auto & Manual Order Assignment',
  'Driver Availability & Shift Management',
  'Route Deviation & SLA Breach Alerts',
  'Failed Delivery Management & Re-attempt',
  'Bulk Order Import via CSV or API',
  'Customer Notification Triggers',
  'Multi-depot Operations View',
  'Real-time Analytics Dashboard',
  'Driver Communication Tools',
  'Custom Reporting & Scheduled Exports',
  'Role-based Access Control',
];

const driverAppFeatures = [
  'Accept / Reject Delivery Tasks',
  'Optimized Turn-by-turn Navigation',
  'Multi-stop Delivery List',
  'POD Capture — Photo, e-Signature & OTP',
  'Barcode / QR Parcel Scan',
  'Cash Collection & Digital Receipt',
  'Return & Reverse Pickup Handling',
  'Delivery Status Updates',
  'Earnings & Performance Dashboard',
  'Call Masking to Customer',
  'Trip History & Route Replay',
  'Multi-Language Support',
];

const comparisonRows = [
  'AI-optimized multi-stop routing',
  'Digital Proof of Delivery (POD)',
  'Real-time shipment tracking',
  'Per-delivery cost breakdown',
  'WMS integration',
  'ML-based ETA prediction',
  'Automated dispatch engine',
  'Demand forecasting',
  'Returns & reverse logistics',
  'Exception & SLA alerts',
];

const integrations = [
  { icon: 'ShoppingCartIcon', label: 'Shopify' },
  { icon: 'ShoppingCartIcon', label: 'WooCommerce' },
  { icon: 'DocumentChartBarIcon', label: 'SAP ERP' },
  { icon: 'DocumentChartBarIcon', label: 'Oracle' },
  { icon: 'DocumentChartBarIcon', label: 'Tally' },
  { icon: 'ArrowPathIcon', label: 'Custom WMS' },
  { icon: 'CreditCardIcon', label: 'Razorpay' },
  { icon: 'CreditCardIcon', label: 'PayU' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'SMS / Email' },
  { icon: 'MapPinIcon', label: 'Google Maps' },
  { icon: 'MapPinIcon', label: 'MapmyIndia' },
];

const metrics = [
  { value: 'AI-powered', label: 'Route Optimization', icon: 'MapIcon' },
  { value: 'Real-time', label: 'Fleet & Shipment Visibility', icon: 'EyeIcon' },
  { value: '21 Days', label: 'Go-Live Timeline', icon: 'BoltIcon' },
  { value: '99.9%', label: 'Platform Uptime SLA', icon: 'ShieldCheckIcon' },
];

export default function LogisticsContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (dark custom) ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: '#0B1426' }}
      >
        {/* circuit grid */}
        <div className="absolute inset-0 circuit-bg opacity-25 pointer-events-none" />
        {/* glow orb left */}
        <div
          className="orb absolute w-[700px] h-[500px] rounded-full opacity-20 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(10,47,216,0.6) 0%, transparent 65%)', top: '-100px', left: '-150px' }}
        />
        {/* glow orb right */}
        <div
          className="orb-2 absolute w-[500px] h-[500px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(27,141,255,0.4) 0%, transparent 70%)', bottom: '-100px', right: '-100px' }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left — text */}
            <div>
              <Link
                href="/products"
                className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/40 text-sm font-medium mb-8 hover:text-white/70 transition-colors"
              >
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1B8DFF]/30 bg-[#1B8DFF]/10 text-[11px] font-semibold tracking-widest text-[#93C5FD] uppercase mb-6">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#1B8DFF] opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#1B8DFF]" />
                </span>
                Logistics Platform
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-6">
                Smarter Logistics,
                <br />
                <span className="gradient-text-animated">Powered by AI</span>
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/55 text-lg leading-relaxed mb-10 max-w-lg">
                End-to-end delivery intelligence — AI route optimization, ML-powered ETAs, warehouse integration, and real-time cost analytics. Built for scale.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4">
                <Link
                  href="/demo-booking"
                  className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold"
                >
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/70 border border-white/15 hover:border-white/35 hover:text-white transition-all"
                >
                  <Icon name="PhoneIcon" size={15} />
                  Talk to Sales
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2 mt-9">
                {['21-day go-live', 'REST API & webhooks', 'Multi-depot support', '99.9% uptime'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/45 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#1B8DFF]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — feature list panel */}
            <div className="animate-on-scroll stagger-3">
              <div
                className="rounded-2xl border border-white/10 overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.035)', backdropFilter: 'blur(16px)' }}
              >
                {/* Panel header */}
                <div className="px-6 py-4 border-b border-white/[0.08] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-white/35 uppercase tracking-widest">Core Intelligence Features</span>
                  <div className="flex gap-1.5">
                    {['bg-red-400/50', 'bg-yellow-400/50', 'bg-green-400/50'].map((c) => (
                      <span key={c} className={`w-2.5 h-2.5 rounded-full ${c}`} />
                    ))}
                  </div>
                </div>

                {/* Feature rows */}
                <div className="divide-y divide-white/[0.05]">
                  {coreFeatures.map((f) => (
                    <div
                      key={f.num}
                      className="flex items-start gap-4 px-6 py-4 hover:bg-white/[0.03] transition-colors group"
                    >
                      <span className="font-mono-custom text-xs text-[#1B8DFF]/50 font-medium pt-0.5 flex-shrink-0 w-5">{f.num}</span>
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                        style={{ background: 'rgba(27,141,255,0.12)', border: '1px solid rgba(27,141,255,0.2)' }}
                      >
                        <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={15} className="text-[#93C5FD]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white/85 leading-snug">{f.label}</p>
                        <span
                          className="inline-block mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full text-[#93C5FD]"
                          style={{ background: 'rgba(27,141,255,0.1)' }}
                        >
                          {f.tag}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #0321A8 0%, #0A2FD8 60%, #0459F7 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {metrics.map((m, i) => (
              <div key={m.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/10">
                  <Icon name={m.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{m.value}</div>
                  <div className="text-xs text-white/60 font-medium">{m.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES (numbered editorial rows) ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 max-w-2xl mb-16">
            <span className="section-label block mb-3">Intelligence Layer</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six AI capabilities that define{' '}
              <span className="italic font-light text-[#0A2FD8]">modern logistics.</span>
            </h2>
          </div>

          <div className="flex flex-col divide-y divide-[rgba(15,23,42,0.07)]">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group py-9 grid md:grid-cols-[72px_1fr_auto] lg:grid-cols-[72px_300px_1fr] gap-6 lg:gap-10 items-start -mx-4 px-4 hover:bg-[rgba(10,47,216,0.02)] transition-colors rounded-2xl cursor-default`}
              >
                {/* Large number */}
                <div
                  className="font-display text-[56px] font-semibold leading-none pt-1 select-none transition-colors"
                  style={{ color: 'rgba(10,47,216,0.12)' }}
                >
                  {f.num}
                </div>

                {/* Icon + title + tag */}
                <div>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ background: 'linear-gradient(135deg, rgba(10,47,216,0.12), rgba(3,33,168,0.06))' }}
                  >
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0A2FD8]" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#0F172A] leading-snug mb-3">{f.label}</h3>
                  <span
                    className="inline-block text-xs font-semibold px-3 py-1 rounded-full text-[#0A2FD8]"
                    style={{ background: 'rgba(10,47,216,0.08)' }}
                  >
                    {f.tag}
                  </span>
                </div>

                {/* Description */}
                <p className="text-[#475569] text-base leading-relaxed pt-1 lg:max-w-xl">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DELIVERY PIPELINE (dark) ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#0B1426' }}>
        <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(10,47,216,0.3) 0%, transparent 65%)', filter: 'blur(60px)' }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1B8DFF]/30 bg-[#1B8DFF]/10 text-[11px] font-semibold tracking-widest text-[#93C5FD] uppercase mb-5">
              Platform Flow
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              Every order source.{' '}
              <span className="italic font-light text-[#93C5FD]">One intelligent platform.</span>
            </h2>
          </div>

          {/* 4-step pipeline */}
          <div className="relative grid md:grid-cols-4 gap-8">
            {/* Connecting line */}
            <div
              className="hidden md:block absolute top-[26px] left-[calc(12.5%+20px)] right-[calc(12.5%+20px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(27,141,255,0.05), rgba(27,141,255,0.35) 25%, rgba(27,141,255,0.35) 75%, rgba(27,141,255,0.05))' }}
            />

            {pipelineSteps.map((step, i) => (
              <div key={step.title} className={`animate-on-scroll stagger-${i + 2} relative flex flex-col items-center text-center`}>
                <div
                  className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-5 z-10"
                  style={{ background: 'linear-gradient(135deg, #0A2FD8, #0321A8)', boxShadow: '0 0 28px rgba(27,141,255,0.28)' }}
                >
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={24} className="text-white" />
                </div>
                <div className="text-[10px] font-semibold text-[#1B8DFF]/50 tracking-widest uppercase mb-2">Step {i + 1}</div>
                <h3 className="font-semibold text-white text-base mb-2">{step.title}</h3>
                <p className="text-sm text-white/45 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Capability pills */}
          <div className="animate-on-scroll stagger-3 mt-16 flex flex-wrap justify-center gap-3">
            {['AI Route Optimized', 'ML-ETA Prediction', 'Live GPS Tracking', 'Digital POD', 'Per-delivery Cost Analytics', 'WMS Integrated'].map((p) => (
              <div
                key={p}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#1B8DFF]/20 bg-white/[0.04]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#1B8DFF]" />
                <span className="text-sm text-white/60 font-medium">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PLATFORM SUITE ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Platform Capabilities</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Complete logistics operations,{' '}
              <span className="italic font-light text-[#0A2FD8]">built in.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left — dark checklist panel */}
            <div
              className="animate-on-scroll stagger-2 rounded-2xl p-8 md:p-10"
              style={{ background: '#0F172A' }}
            >
              <h3 className="font-display text-xl font-semibold text-white mb-7">Movio Logistics ensures your operations deliver:</h3>
              <div className="flex flex-col gap-4">
                {platformBenefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: 'linear-gradient(135deg, #0A2FD8, #0321A8)' }}
                    >
                      <Icon name="CheckIcon" size={10} className="text-white" />
                    </div>
                    <span className="text-sm text-white/65 leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/[0.08]">
                <p className="text-[#1B8DFF] font-semibold text-sm">Smarter Routes. Lower Costs. Better Deliveries.</p>
              </div>
            </div>

            {/* Right — capability chips */}
            <div className="animate-on-scroll stagger-3 flex flex-col gap-4">
              <h3 className="font-semibold text-sm uppercase tracking-widest text-[#64748B] mb-2">Additional Capabilities</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {platformCapabilities.map((c) => (
                  <div
                    key={c.label}
                    className="flex items-center gap-3 p-4 rounded-xl border border-[rgba(15,23,42,0.07)] bg-[#F5F5F5] hover:border-[rgba(10,47,216,0.25)] hover:bg-[rgba(10,47,216,0.03)] transition-all group"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: 'linear-gradient(135deg, rgba(10,47,216,0.12), rgba(3,33,168,0.06))' }}
                    >
                      <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-[#0A2FD8]" />
                    </div>
                    <span className="text-sm font-semibold text-[#0F172A]">{c.label}</span>
                  </div>
                ))}
              </div>

              {/* Mini stat row */}
              <div className="mt-2 grid grid-cols-2 gap-3">
                {[
                  { icon: 'EyeIcon', title: 'Complete Visibility', desc: 'Track every parcel and driver in real time' },
                  { icon: 'AdjustmentsHorizontalIcon', title: 'Full Control', desc: 'Centralized dispatch and policy enforcement' },
                ].map((c) => (
                  <div
                    key={c.title}
                    className="rounded-xl p-5 border border-[rgba(10,47,216,0.15)]"
                    style={{ background: 'linear-gradient(135deg, rgba(10,47,216,0.05), rgba(3,33,168,0.02))' }}
                  >
                    <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0A2FD8] mb-3" />
                    <p className="font-semibold text-sm text-[#0F172A] mb-1">{c.title}</p>
                    <p className="text-xs text-[#475569]">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── DISPATCHER & DRIVER (side by side, no tabs) ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Operational Apps</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Built for Dispatchers.{' '}
              <span className="italic font-light text-[#0A2FD8]">Loved by Drivers.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Two purpose-built apps that keep every delivery on time and every cost visible.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Dispatcher Panel */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm">
              <div
                className="p-6"
                style={{ background: 'linear-gradient(135deg, #0A2FD8, #0321A8)' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="ComputerDesktopIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Dispatcher Panel</h3>
                    <p className="text-white/60 text-xs">Web-based operations control center</p>
                  </div>
                </div>
              </div>
              <div className="p-5 bg-white grid sm:grid-cols-2 gap-2">
                {dispatcherFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-[#F5F5F5] border border-[rgba(15,23,42,0.06)]">
                    <div
                      className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #0A2FD8, #0321A8)' }}
                    >
                      <Icon name="CheckIcon" size={10} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A] leading-snug">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Driver App */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm">
              <div className="p-6 bg-[#0F172A]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon name="DevicePhoneMobileIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Driver App</h3>
                    <p className="text-white/50 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 bg-white grid sm:grid-cols-2 gap-2">
                {driverAppFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-[#F5F5F5] border border-[rgba(15,23,42,0.06)]">
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F172A]">
                      <Icon name="CheckIcon" size={10} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A] leading-snug">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── COMPARISON TABLE ── */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Comparison</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Traditional Logistics vs{' '}
              <span className="italic font-light text-[#0A2FD8]">Movio Logistics</span>
            </h2>
          </div>

          <div className="animate-on-scroll stagger-2 rounded-2xl border border-[rgba(15,23,42,0.08)] overflow-hidden shadow-sm">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_100px_100px] border-b-2 border-[rgba(15,23,42,0.07)]">
              <div className="px-6 py-4 text-xs font-semibold text-[#94A3B8] uppercase tracking-widest">Feature</div>
              <div className="px-4 py-4 text-center bg-[#FFF7ED] border-l border-[rgba(15,23,42,0.07)]">
                <span className="text-xs font-semibold text-[#D97706] uppercase tracking-widest">Manual</span>
              </div>
              <div
                className="px-4 py-4 text-center border-l border-[rgba(10,47,216,0.12)]"
                style={{ background: 'rgba(10,47,216,0.06)' }}
              >
                <span className="text-xs font-semibold text-[#0A2FD8] uppercase tracking-widest">Movio</span>
              </div>
            </div>

            {comparisonRows.map((row, i) => (
              <div
                key={row}
                className={`grid grid-cols-[1fr_100px_100px] border-b border-[rgba(15,23,42,0.05)] ${i % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white'}`}
              >
                <div className="px-6 py-4 text-sm font-medium text-[#0F172A]">{row}</div>
                <div className="px-4 py-4 flex items-center justify-center border-l border-[rgba(15,23,42,0.05)] bg-[#FFF7ED]">
                  <div className="w-6 h-6 rounded-full bg-[#FEF3C7] flex items-center justify-center">
                    <Icon name="XMarkIcon" size={12} className="text-[#D97706]" />
                  </div>
                </div>
                <div
                  className="px-4 py-4 flex items-center justify-center border-l border-[rgba(10,47,216,0.08)]"
                  style={{ background: 'rgba(10,47,216,0.03)' }}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #0A2FD8, #0321A8)' }}
                  >
                    <Icon name="CheckIcon" size={10} className="text-white" />
                  </div>
                </div>
              </div>
            ))}

            {/* Footer */}
            <div className="grid grid-cols-[1fr_100px_100px] bg-white">
              <div className="px-6 py-4" />
              <div className="px-4 py-4 text-center bg-[#FFF7ED] border-l border-[rgba(15,23,42,0.05)]">
                <span className="text-xs text-[#D97706] font-semibold">0 / {comparisonRows.length}</span>
              </div>
              <div
                className="px-4 py-4 text-center border-l border-[rgba(10,47,216,0.08)]"
                style={{ background: 'rgba(10,47,216,0.05)' }}
              >
                <span className="text-xs text-[#0A2FD8] font-semibold">{comparisonRows.length} / {comparisonRows.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── INTEGRATIONS ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="section-label block mb-3">Integrations</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Connects with your{' '}
              <span className="italic font-light text-[#0A2FD8]">existing tech stack.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-lg mx-auto">No rip-and-replace. Movio integrates with the tools your business already runs on.</p>
          </div>

          <div className="animate-on-scroll stagger-2 flex flex-wrap justify-center gap-3 mb-8">
            {integrations.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white border border-[rgba(15,23,42,0.08)] hover:border-[rgba(10,47,216,0.25)] hover:bg-[rgba(10,47,216,0.04)] transition-all group cursor-default"
              >
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-[#0A2FD8]" />
                <span className="text-sm font-semibold text-[#475569] group-hover:text-[#0A2FD8] transition-colors">{item.label}</span>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-3 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-white border border-[rgba(10,47,216,0.15)]">
              <Icon name="PuzzlePieceIcon" size={16} className="text-[#0A2FD8]" />
              <span className="text-sm font-medium text-[#475569]">Custom integrations available via REST API and webhooks</span>
            </div>
          </div>
        </div>
      </div>

{/* ── CTA ── */}
<div className="py-24 bg-white">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">    <div
      className="relative overflow-hidden rounded-3xl py-14 px-5 md:px-8"
      style={{ background: '#0B1426' }}
    >
      {/* Background Effects */}
      <div className="absolute inset-0 circuit-bg opacity-20 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(10,47,216,0.4) 0%, transparent 70%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <div className="animate-on-scroll stagger-1">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1B8DFF]/30 bg-[#1B8DFF]/10 text-[11px] font-semibold tracking-widest text-[#93C5FD] uppercase mb-6">
            <span className="relative flex h-1.5 w-1.5">
              <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#1B8DFF] opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#1B8DFF]" />
            </span>
            Ready to Launch?
          </span>

          <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-5">
            Your branded logistics platform,{' '}
            <span className="italic font-light text-[#93C5FD]">
              live in 21 days.
            </span>
          </h2>

          <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            For demo or more information
          </p>

          {/* Contact Info */}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 mb-10 text-sm">
            <a
              href="tel:+919600830488"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <Icon name="PhoneIcon" size={14} />
              +91 9600830488
            </a>

            <span className="hidden sm:block text-white/20">|</span>

            <a
              href="mailto:contact@movio.in"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <Icon name="EnvelopeIcon" size={14} />
              contact@movio.in
            </a>

            <span className="hidden sm:block text-white/20">|</span>

            <a
              href="https://www.movio.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <Icon name="GlobeAltIcon" size={14} />
              www.movio.in
            </a>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/demo-booking"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#0A2FD8] transition-all duration-300 hover:bg-[#F8FAFC] hover:-translate-y-0.5 hover:shadow-xl"
            >
              Book Free Demo
              <Icon name="ArrowRightIcon" size={18} />
            </Link>

            <Link
              href="/contact"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border border-white/20 transition-all duration-300 hover:bg-white/10 hover:border-white/40"
            >
              <Icon name="PhoneIcon" size={16} />
              Talk to Sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
</div></div>
  );
}
